import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ActivenessDistributionCard from '../../../components/ActivenessDistributionCard';

const SkeletonBox = ({ className = '' }) => (
  <div className={`rounded animate-pulse ${className}`} style={{ backgroundColor: '#1f1f1f' }} />
);

const fmtUSD = (n) => {
  if (!n) return '$0';
  return '$' + Math.round(n)?.toLocaleString('fr-FR');
};

const LEVEL_COLORS = ['#6b7280', '#22c55e', '#16a34a', '#15803d', '#166534', '#14532d'];
const LEVEL_LABELS = ['Aucun', 'L1', 'L2', 'L3', 'L4', 'L5'];

const getCurrentPalierBonus = (pct, paliers) => {
  if (pct == null || !paliers?.length) return 0;
  let bonus = 0;
  for (const p of paliers) {
    if (pct >= p?.pct) bonus = p?.bonus || 0;
    else break;
  }
  return bonus;
};

const ObjectifsADateSection = ({
  currentData,
  serversData,
  selectedServer,
  targets,
  incrementalConfig,
  fallbackPaliers,
  loading,
}) => {
  const { t } = useTranslation();
  const data = useMemo(() => {
    if (!currentData?.length) return null;

    // ── ACTIVENESS ──────────────────────────────────────────────────────────
    const levelCounts = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    currentData?.forEach((r) => {
      const lvl = Math.min(r?.activeness_level || 0, 5);
      levelCounts[lvl] = (levelCounts?.[lvl] || 0) + 1;
    });
    const totalBonusActiveness = currentData?.reduce(
      (sum, r) => sum + (r?.bonus_activeness || 0),
      0
    );
    const eligibleCount = currentData?.filter((r) => (r?.activeness_level || 0) >= 1)?.length;
    const maxLevelCount = currentData?.filter((r) => (r?.activeness_level || 0) >= 5)?.length;

    // ── RANKUP ──────────────────────────────────────────────────────────────
    const rankingUp = currentData?.filter((r) => (r?.tier_current || 0) > (r?.tier_m1 || 0) && (r?.bonus_rankup || 0) > 0)?.length;
    const maintaining = currentData?.filter((r) =>
      (r?.tier_current || 0) === (r?.tier_m1 || 0) && (r?.tier_m1 || 0) >= 2 && (r?.bonus_rankup || 0) > 0
    )?.length;
    const totalBonusRankup = currentData?.reduce(
      (sum, r) => sum + (r?.bonus_rankup || 0),
      0
    );

    // ── INCREMENTAL BONUS ───────────────────────────────────────────────────
    const serversToCalc =
      selectedServer === 'Tous'
        ? (serversData || [])?.map((s) => s?.id)
        : [selectedServer];

    let incrementalBonus = 0;
    serversToCalc?.forEach((srvId) => {
      const target = targets?.[srvId] || 0;
      if (!target) return;
      const totalDiamonds = currentData
        ?.filter((r) => r?.server === srvId)
        ?.reduce((sum, r) => sum + (r?.diamonds || 0), 0);
      if (!totalDiamonds) return;

      const pct = (totalDiamonds / target) * 100;

      // Get paliers for this server from incremental_config
      const serverConfig = (incrementalConfig || [])?.find((p) => p?.server === srvId);
      const serverPaliers = serverConfig?.completion_tiers || [];
      const paliers = serverPaliers?.length > 0 ? serverPaliers : fallbackPaliers;
      let bonusPct = 0;
      for (const p of paliers) {
        if (pct >= p?.pct) bonusPct = p?.bonus || 0;
        else break;
      }
      if (bonusPct > 0) {
        incrementalBonus += (totalDiamonds / 100) * (bonusPct / 100);
      }
    });

    // ── DAYS ────────────────────────────────────────────────────────────────
    const daysElapsed = currentData?.reduce((max, r) => Math.max(max, r?.days_elapsed || 0), 0) || 1;
    const daysInMonth = 30;

    const totalBonus = totalBonusActiveness + totalBonusRankup + incrementalBonus;
    const projectedBonus = daysElapsed > 0 ? (totalBonus / daysElapsed) * daysInMonth : 0;

    return {
      levelCounts,
      totalBonusActiveness,
      eligibleCount,
      maxLevelCount,
      rankingUp,
      maintaining,
      totalBonusRankup,
      incrementalBonus,
      totalBonus,
      projectedBonus,
      daysElapsed,
      totalCreators: currentData?.length,
    };
  }, [currentData, serversData, selectedServer, targets, incrementalConfig, fallbackPaliers]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3]?.map((i) => (
          <div
            key={i}
            className="rounded-xl border p-5 animate-pulse"
            style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', height: '120px' }}
          />
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="rounded-xl p-5 border flex items-center justify-center h-24"
        style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}
      >
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performanceActuelle.noDataAvailable', 'No data available')}
        </p>
      </div>
    );
  }

  const {
    levelCounts,
    totalBonusActiveness,
    eligibleCount,
    maxLevelCount,
    rankingUp,
    maintaining,
    totalBonusRankup,
    incrementalBonus,
    totalBonus,
    projectedBonus,
    daysElapsed,
    totalCreators,
  } = data;

  return (
    <div className="space-y-4">
      {/* ACTIVENESS CARD */}
      <ActivenessDistributionCard
        distribution={levelCounts}
        totalBonus={totalBonusActiveness}
        title={t('admin.performanceActuelle.activenessTitle', { count: totalCreators })}
      />

      {/* RANKUP CARD */}
      <div className="rounded-xl border p-5" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('admin.performanceActuelle.rankupTitle', { count: rankingUp + maintaining })}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
              {t('admin.performanceActuelle.basedOnTierStatus')}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>{t('admin.performanceActuelle.bonusRankupTotal')}</p>
            <p className="text-base font-bold" style={{ color: '#f97316', fontFamily: 'Sora, sans-serif' }}>
              {fmtUSD(totalBonusRankup)}
            </p>
          </div>
        </div>
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)' }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-base">↑</span>
              <span className="text-xs font-semibold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>{t('admin.performanceActuelle.tierUp')}</span>
            </div>
            <p className="text-lg font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
              {rankingUp} <span className="text-xs font-normal" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('admin.performanceActuelle.creatorsLabel')}</span>
            </p>
          </div>
          <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)' }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-base">→</span>
              <span className="text-xs font-semibold" style={{ color: '#3b82f6', fontFamily: 'Sora, sans-serif' }}>{t('admin.performanceActuelle.tierMaintain')}</span>
            </div>
            <p className="text-lg font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
              {maintaining} <span className="text-xs font-normal" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('admin.performanceActuelle.creatorsLabel')}</span>
            </p>
          </div>
        </div>
      </div>

      {/* BONUS TOTAL CARD */}
      <div className="rounded-xl border p-5" style={{ backgroundColor: '#111111', borderColor: 'rgba(229,32,42,0.3)' }}>
        <h3 className="text-sm font-bold mb-4" style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'Sora, sans-serif' }}>
          {t('admin.performanceActuelle.bonusTotalEstimated')}
        </h3>
        <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <div className="rounded-lg p-3" style={{ backgroundColor: '#1a1a1a' }}>
            <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{t('admin.performanceActuelle.activenessLabel', 'Activeness')}</p>
            <p className="text-sm font-bold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>{fmtUSD(totalBonusActiveness)}</p>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: '#1a1a1a' }}>
            <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{t('dashboard.rankup', 'Rank-up')}</p>
            <p className="text-sm font-bold" style={{ color: '#f97316', fontFamily: 'Sora, sans-serif' }}>{fmtUSD(totalBonusRankup)}</p>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: '#1a1a1a' }}>
            <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{t('admin.performanceActuelle.incrementalLabel', 'Incremental')}</p>
            <p className="text-sm font-bold" style={{ color: '#3b82f6', fontFamily: 'Sora, sans-serif' }}>{fmtUSD(incrementalBonus)}</p>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(229,32,42,0.08)', border: '1px solid rgba(229,32,42,0.2)' }}>
            <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>{t('admin.performanceActuelle.totalAgency')}</p>
            <p className="text-xl font-bold" style={{ color: '#E5202A', fontFamily: 'Sora, sans-serif' }}>{fmtUSD(totalBonus)}</p>
          </div>
        </div>
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performanceActuelle.basedOnDays', { days: daysElapsed })}{' '}
          <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>{fmtUSD(projectedBonus)}</span>
        </p>
      </div>
    </div>
  );
};

export default ObjectifsADateSection;
